import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Guid } from 'guid-typescript';
import { Project, ProjectBudget } from '../../../../../../common/models/project';
import { ProjectService } from '../../../../../../common/services/project.service';
import { ProjectBudgetStatus } from '../../../../../../common/enums/project-budget-status';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { GenericDialogComponent } from '../../../../../../common/components/dialog/dialog';


@Component({
  standalone: true,
  selector: 'app-project-budgets',
  templateUrl: './budgets.component.html',
  styleUrls: ['./budgets.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    MatSnackBarModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatDialogModule
  ]
})
export class ProjectBudgetsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  projectId!: string;
  projectBudgets: ProjectBudget[] = [];
  filteredBudgets: ProjectBudget[] = [];
  isHandset: boolean = false;

  public ProjectBudgetStatus = ProjectBudgetStatus;

  projectApprovedAmount: number = 0;

  isBudgetSynced(budget: ProjectBudget): boolean {
    return budget.isSynced(this.projectApprovedAmount);
  }

  private readonly snackbarConfig: MatSnackBarConfig = {
    duration: 3000,
    horizontalPosition: 'right',
    verticalPosition: 'top'
  };

  constructor(
    private projectService: ProjectService,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private router: Router,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {

    this.projectId = this.route.snapshot.paramMap.get('projectId') || '';
    this.fetchBudgets(this.projectId);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
   }


  private fetchBudgets(projectId: string): void {
    forkJoin({
        project: this.projectService.getProject(Guid.parse(projectId)),
        projectBudgets: this.projectService.getProjectBudgets(Guid.parse(projectId))
    }).pipe(takeUntil(this.destroy$)).subscribe({
        next: ({ project, projectBudgets }) => {
          this.projectApprovedAmount = project.approvedAmount;
          this.projectBudgets = projectBudgets.map(b => ProjectBudget.fromJson(b));
          this.filteredBudgets = [...this.projectBudgets];
        },
        error: (err) => {
          console.error('Error loading project budgets', err);
          this.snackBar.open('Došlo je do greške prilikom učitavanja projektnog proračuna.', 'Zatvori', this.snackbarConfig);
        }
      });
  }

  applySearch(searchTerm: string): void {
    searchTerm = searchTerm.trim().toLowerCase();
    if (searchTerm) {
        this.filteredBudgets = this.projectBudgets.filter((budget: ProjectBudget) => {
        return (
            true
        );
        });
    } else {
        this.filteredBudgets = [...this.projectBudgets];
    }
  }

  confirmReturn(budget: ProjectBudget): void {
    const dialogRef = this.dialog.open(GenericDialogComponent, {
      width: '50%',
      data: {
        title: 'Potvrdite vraćanje',
        message: `Jeste li sigurni da želite vratiti proračun verzija broj ${budget.budgetVersion} na doradu?`,
        confirmButtonText: 'Da',
        cancelButtonText: 'Ne',
      },
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (result === true) {
        this.returnBudget(budget);
      }
    });
  }

  private returnBudget(budget: ProjectBudget): void {
    this.projectService.changeProjectBudgetStatus(Guid.parse(this.projectId), Guid.parse(budget.id), { targetStatus: ProjectBudgetStatus.Returned }).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.fetchBudgets(this.projectId);
          this.snackBar.open('Proračun je uspješno vraćen na doradu.', 'Zatvori', this.snackbarConfig);
        },
        error: (err) => {
          console.error('Error rejecting budget', err);
          this.snackBar.open('Došlo je do greške pri vraćanju proračuna na doradu.', 'Zatvori', this.snackbarConfig);
        }
      });
  } 

  confirmApprove(budget: ProjectBudget): void {
    const dialogRef = this.dialog.open(GenericDialogComponent, {
      width: '50%',
      data: {
        title: 'Potvrdite odobravanje',
        message: `Jeste li sigurni da želite odobriti proračun verzija broj ${budget.budgetVersion}?`,
        confirmButtonText: 'Da',
        cancelButtonText: 'Ne',
      },
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (result === true) {
        this.approveBudget(budget);
      }
    });
  }

  private approveBudget(budget: ProjectBudget): void {
    this.projectService.changeProjectBudgetStatus(Guid.parse(this.projectId), Guid.parse(budget.id), { targetStatus: ProjectBudgetStatus.Approved }).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.fetchBudgets(this.projectId);
          this.snackBar.open('Proračun je uspješno odobren.', 'Zatvori', this.snackbarConfig);
        },
        error: (err) => {
          console.error('Error approving budget', err);
          this.snackBar.open('Došlo je do greške pri odobravanju proračuna.', 'Zatvori', this.snackbarConfig);
        }
      });
  }

  confirmReject(budget: ProjectBudget): void {
    const dialogRef = this.dialog.open(GenericDialogComponent, {
      width: '50%',
      data: {
        title: 'Potvrdite odbijanje',
        message: `Jeste li sigurni da želite odbiti proračun verzija broj ${budget.budgetVersion}?`,
        confirmButtonText: 'Da',
        cancelButtonText: 'Ne',
      },
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (result === true) {
        this.rejectBudget(budget);
      }
    });
  }

  private rejectBudget(budget: ProjectBudget): void {
    this.projectService.changeProjectBudgetStatus(Guid.parse(this.projectId), Guid.parse(budget.id), { targetStatus: ProjectBudgetStatus.Rejected }).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.fetchBudgets(this.projectId);
          this.snackBar.open('Proračun je uspješno odbijen.', 'Zatvori', this.snackbarConfig);
        },
        error: (err) => {
          console.error('Error rejecting budget', err);
          this.snackBar.open('Došlo je do greške pri odbijanju proračuna.', 'Zatvori', this.snackbarConfig);
        }
      });
  } 

  downloadBudget(projectBudget: ProjectBudget): void {
    this.projectService.downloadProjectBudget(this.projectId, projectBudget.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'B2 Obrazac proračuna programa ili projekta.xls';
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('Error downloading project budget', err);
      }
    });
  }
  
  goToViewBudget(projectBudget: ProjectBudget): void {
     this.router.navigate(['municipality', 'projects', this.projectId, 'budgets', projectBudget.id, 'view']);
  }

}
